/**
 * Medical Necessity Amplitude / BigQuery analytics facade.
 * @module medicalNecessityAnalytics/useMedicalNecessityAnalytics
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { RootState } from "../../../redux/store/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../../../store/UserGeneralInfoStore";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import { createFeedbackTrackers } from "./feedbackTrackers";
import { pruneGuidelineReviewVersionsExcept } from "./guidelineReviewVersion";
import { createGuidelineTrackers } from "./guidelineTrackers";
import { MedicalNecessityAnalyticsSession } from "./sessionState";
import { createViewTrackers } from "./viewTrackers";

/** Inputs for {@link useMedicalNecessityAnalytics}. */
export interface UseMedicalNecessityAnalyticsArgs {
  orderId: string;
  patientId?: string;
}

/**
 * Provides track helpers for the Medical Necessity Analysis screen.
 *
 * Dual-writes via {@link apiWorkerManager.enqueueAndProcess} (BigQuery + Amplitude).
 * `review_session_id` is minted per mount; `guideline_review_version` survives in
 * sessionStorage for the tab (other orders' keys are pruned on mount).
 *
 * @param args - Order and patient identifiers
 * @returns Analytics session helpers and track methods
 */
export const useMedicalNecessityAnalytics = ({
  orderId,
  patientId,
}: UseMedicalNecessityAnalyticsArgs) => {
  const user = useSelector(
    (state: RootState) => state.firebaseAuthentication.user,
  );
  const sessionRef = useRef<MedicalNecessityAnalyticsSession | null>(null);
  if (!sessionRef.current) {
    sessionRef.current = new MedicalNecessityAnalyticsSession();
  }
  const session = sessionRef.current;
  const analysisId = orderId;

  useEffect(() => {
    if (analysisId) {
      pruneGuidelineReviewVersionsExcept(analysisId);
    }
  }, [analysisId]);

  const baseIds = useMemo(
    () => ({
      patient_id: patientId ?? "",
      order_id: orderId,
      org_id: getOrgIdForFetchExternalWorklist() || "",
      user_id:
        user?.id ?? UserStore.getItem(UserGeneralInfoContextKeys.USER_ID) ?? "",
      user_email: user?.email ?? "",
    }),
    [orderId, patientId, user?.email, user?.id],
  );

  const emit = useCallback(
    (eventName: EventsName, additional: Record<string, unknown>) => {
      if (!orderId) return;
      const cleaned: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(additional)) {
        if (value === undefined || value === "") continue;
        if (Array.isArray(value) && value.length === 0) continue;
        cleaned[key] = value;
      }
      apiWorkerManager.enqueueAndProcess({
        ...baseIds,
        event_name: eventName,
        additional_data: {
          screen_name: ScreenNamesForBigQuery.MEDICAL_NECESSITY_ANALYSIS,
          analysis_id: analysisId,
          review_session_id: session.reviewSessionId,
          ...cleaned,
        },
      });
    },
    [analysisId, baseIds, orderId, session.reviewSessionId],
  );

  return useMemo(() => {
    const deps = { analysisId, session, emit };
    return {
      analysisId,
      reviewSessionId: session.reviewSessionId,
      ...createViewTrackers(deps),
      ...createFeedbackTrackers(deps),
      ...createGuidelineTrackers(deps),
    };
  }, [analysisId, emit, session]);
};

/** Return type of {@link useMedicalNecessityAnalytics}. */
export type MedicalNecessityAnalytics = ReturnType<
  typeof useMedicalNecessityAnalytics
>;
